import React from 'react';

/**
 * MB 968 GROUP — premium brand mark
 * Custom geometric monogram with gold accents on deep navy.
 * Sizes: sm (32), md (44), lg (72), xl (96)
 */
export default function Logo({ size = 'md', showText = false, layout = 'inline', className = '' }) {
  const sizes = { sm: 32, md: 44, lg: 72, xl: 96 };
  const px = sizes[size] || sizes.md;
  const id = `mb-${size}`;

  const mark = (
    <svg
      width={px}
      height={px}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-[0_8px_20px_rgba(212,175,55,0.25)]"
    >
      <defs>
        {/* Outer dark navy gradient */}
        <linearGradient id={`bg-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E293B" />
          <stop offset="50%" stopColor="#0F172A" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>
        {/* Gold metallic gradient for stroke and letter */}
        <linearGradient id={`gold-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FCD34D" />
          <stop offset="35%" stopColor="#F59E0B" />
          <stop offset="70%" stopColor="#D97706" />
          <stop offset="100%" stopColor="#B45309" />
        </linearGradient>
        {/* Subtle inner shine */}
        <radialGradient id={`shine-${id}`} cx="0.3" cy="0.2" r="0.8">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        {/* Highlight on gold letter */}
        <linearGradient id={`hl-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FEF3C7" stopOpacity="0.7" />
          <stop offset="50%" stopColor="#FEF3C7" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Hexagonal shield base */}
      <path
        d="M32 2 L58 16 L58 48 L32 62 L6 48 L6 16 Z"
        fill={`url(#bg-${id})`}
        stroke={`url(#gold-${id})`}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* Inner shine overlay */}
      <path
        d="M32 2 L58 16 L58 48 L32 62 L6 48 L6 16 Z"
        fill={`url(#shine-${id})`}
      />

      {/* Inner thin gold border */}
      <path
        d="M32 7 L53.5 18.5 L53.5 45.5 L32 57 L10.5 45.5 L10.5 18.5 Z"
        fill="none"
        stroke={`url(#gold-${id})`}
        strokeWidth="0.5"
        strokeOpacity="0.45"
        strokeLinejoin="round"
      />

      {/* Custom "M" monogram — geometric, refined */}
      {/* Left vertical */}
      <path
        d="M18 44 L18 21 L24 21 L32 33 L40 21 L46 21 L46 44 L41 44 L41 29 L33.5 40.5 L30.5 40.5 L23 29 L23 44 Z"
        fill={`url(#gold-${id})`}
        stroke="#92400E"
        strokeWidth="0.3"
        strokeLinejoin="round"
      />
      {/* Highlight on top of M */}
      <path
        d="M18 21 L18 28 L23 28 L23 23 L30 33 L34 33 L41 23 L41 28 L46 28 L46 21 L40 21 L32 33 L24 21 Z"
        fill={`url(#hl-${id})`}
        opacity="0.6"
      />

      {/* Bottom accent line */}
      <line x1="14" y1="50" x2="50" y2="50" stroke={`url(#gold-${id})`} strokeWidth="0.6" strokeOpacity="0.5" />

      {/* 968 tiny subscript dots — premium touch */}
      <circle cx="22" cy="53.5" r="0.9" fill={`url(#gold-${id})`} />
      <circle cx="32" cy="53.5" r="0.9" fill={`url(#gold-${id})`} />
      <circle cx="42" cy="53.5" r="0.9" fill={`url(#gold-${id})`} />
    </svg>
  );

  if (!showText) {
    return <div className={`inline-flex ${className}`}>{mark}</div>;
  }

  if (layout === 'stacked') {
    return (
      <div className={`inline-flex flex-col items-center gap-2 ${className}`}>
        {mark}
        <div className="text-center leading-tight">
          <div className="font-bold tracking-wider" style={{
            fontSize: px * 0.34,
            background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 50%, #D97706 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}>
            MB 968
          </div>
          <div className="text-[10px] uppercase text-slate-500 tracking-[0.32em] font-semibold mt-1">
            GROUP
          </div>
        </div>
      </div>
    );
  }

  // inline layout
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      {mark}
      <div className="leading-tight">
        <div className="font-bold tracking-wide" style={{
          fontSize: px * 0.32,
          background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 60%, #D97706 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
        }}>
          MB 968
        </div>
        <div className="text-[9px] uppercase text-slate-500 tracking-[0.28em] font-semibold mt-0.5">
          GROUP
        </div>
      </div>
    </div>
  );
}
